import SEO from "../components/SEO";
import { Breadcrumbs } from "../components/Shared";
import { BRAND } from "../data/siteData";
import { Download } from "lucide-react";

const WARRANTY_PDF = "/vantyx-lifetime-warranty.pdf";

const Legal = ({ title, path, indexable = false, children }) => (
  <>
    <SEO title={`${title} | Vantyx Coatings`} description={`${title} for Vantyx Coatings LLC.`} path={path} noindex={!indexable} />
    <section className="bg-white py-20 md:py-28 px-6 md:px-12 min-h-[60vh]">
      <div className="max-w-3xl mx-auto">
        <Breadcrumbs items={[{ name: "Home", path: "/" }, { name: title, path }]} />
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-[#000000] mb-8">{title}</h1>
        <div className="space-y-5 text-gray-600 text-sm leading-relaxed">{children}</div>
      </div>
    </section>
  </>
);

export const PrivacyPage = () => (
  <Legal title="Privacy Policy" path="/privacy-policy">
    <p>Last updated: {new Date().toLocaleDateString("en-US", { month: "long", year: "numeric" })}</p>
    <p>Vantyx Coatings LLC ("we," "us") respects your privacy. This policy explains what we collect and how we use it.</p>
    <h2 className="font-display text-xl font-bold text-[#000000] pt-4">Information We Collect</h2>
    <p>When you submit an estimate request or contact form, we collect your name, phone number, email address, city, garage size, and how you heard about us. We also collect standard analytics data (pages visited, device type) via analytics tools.</p>
    <h2 className="font-display text-xl font-bold text-[#000000] pt-4">How We Use It</h2>
    <p>We use your information solely to respond to your inquiry, schedule estimates, and provide our services. Lead information is transmitted to our customer relationship management system (GoHighLevel) to manage follow-up. We do not sell your personal information.</p>
    <h2 className="font-display text-xl font-bold text-[#000000] pt-4">Contact</h2>
    <p>Questions? Email {BRAND.email} or call {BRAND.phone}.</p>
  </Legal>
);

export const WarrantyPage = () => (
  <Legal title="Lifetime Limited Warranty" path="/warranty" indexable>
    <div className="not-prose flex flex-col sm:flex-row items-start sm:items-center gap-4 p-5 bg-[#000000] border-2 border-[#E8B86D] rounded-lg -mt-2 mb-2">
      <div className="flex-1">
        <p className="text-white font-display font-bold text-base leading-tight m-0">Take it with you.</p>
        <p className="text-gray-400 text-xs mt-1 m-0">Download the full warranty as a PDF — easy to share with your spouse or keep for your records.</p>
      </div>
      <a
        href={WARRANTY_PDF}
        download="Vantyx-Lifetime-Limited-Warranty.pdf"
        data-testid="warranty-pdf-download"
        className="inline-flex items-center gap-2 bg-[#E8B86D] text-[#000000] hover:bg-white transition-colors duration-200 px-5 py-3 font-display font-bold uppercase tracking-wider text-xs rounded-full whitespace-nowrap"
      >
        <Download size={16} strokeWidth={2.5} /> Download PDF
      </a>
    </div>
    <p>Last updated: {new Date().toLocaleDateString("en-US", { month: "long", year: "numeric" })}</p>
    <p>
      Every Vantyx Coatings TRI-BOND™ floor is backed by our written Lifetime Limited Warranty.
      We stand behind the chemistry, the prep, and the workmanship — for as long as you own your home.
    </p>

    <h2 className="font-display text-xl font-bold text-[#000000] pt-4">What's Covered</h2>
    <p>For the lifetime of original homeowner ownership of the property where the coating was installed, Vantyx Coatings LLC warrants the TRI-BOND™ polyaspartic floor system against:</p>
    <ul className="list-disc pl-5 space-y-1">
      <li><strong>Delamination</strong> — the coating separating from properly prepped concrete.</li>
      <li><strong>Peeling or blistering</strong> under normal residential use.</li>
      <li><strong>Hot-tire pickup</strong> — the coating lifting where a hot tire sits.</li>
      <li><strong>Yellowing, ambering or chalking</strong> of the UV-stable topcoat.</li>
      <li><strong>Workmanship defects</strong> in the diamond grinding, flake broadcast or topcoat application.</li>
    </ul>

    <h2 className="font-display text-xl font-bold text-[#000000] pt-4">How a Claim Is Handled</h2>
    <p>
      If a covered defect appears, contact us at {BRAND.phone} or {BRAND.email} with photos and a brief description.
      A Vantyx technician will inspect the floor at no cost, and any repair covered by this warranty will be performed at no charge to you —
      including materials, labor and cleanup. Most warranty repairs are completed in a single visit.
    </p>

    <h2 className="font-display text-xl font-bold text-[#000000] pt-4">What's Not Covered</h2>
    <p>This warranty is limited. It does not cover damage caused by conditions outside the coating itself, including:</p>
    <ul className="list-disc pl-5 space-y-1">
      <li>Structural movement or settling of the concrete slab, new cracks caused by soil movement, or slab heave.</li>
      <li>Water intrusion from outside the garage (grading, drainage, slab-edge seepage, flooding).</li>
      <li>Damage from moisture vapor emission exceeding the levels present at time of install, or from a permanently wet slab.</li>
      <li>Abuse or misuse: dragging sharp steel, welding sparks, dropped tools, battery acid, brake fluid, harsh solvents (acetone, MEK, paint stripper), pressure washing above 2,000 PSI, or steam cleaning.</li>
      <li>Commercial or industrial use unless specifically quoted and installed as a commercial system.</li>
      <li>Coating damage from fire, flood, earthquake or other acts of nature.</li>
      <li>Cosmetic wear consistent with normal use — light surface scratches, dulling in high-traffic zones, or embedded debris under tire scrub.</li>
    </ul>

    <h2 className="font-display text-xl font-bold text-[#000000] pt-4">Transferability</h2>
    <p>
      This warranty is provided to the original homeowner at the original installation address.
      It may be transferred one time to a subsequent owner of the home within the first ten (10) years of installation
      by submitting a written request to Vantyx Coatings within 30 days of the property closing.
      Transferred warranty coverage is limited to ten (10) years from the original install date.
    </p>

    <h2 className="font-display text-xl font-bold text-[#000000] pt-4">Reasonable Care</h2>
    <p>
      To keep your warranty in effect, follow simple care guidelines: dust mop or blow the floor for routine cleaning,
      use warm water with a mild pH-neutral cleaner for washing, wipe up automotive fluids within a reasonable time,
      and place protective mats under jack stands or heavy stationary equipment. No waxing, sealing or aftermarket coatings are required — or permitted under this warranty.
    </p>

    <h2 className="font-display text-xl font-bold text-[#000000] pt-4">Limitation of Remedy</h2>
    <p>
      The sole remedy under this warranty is repair or, at Vantyx Coatings LLC's option, replacement of the affected coating area.
      Vantyx Coatings LLC is not liable for incidental or consequential damages, loss of use of the garage, or damage to vehicles or contents.
      This warranty is in lieu of all other warranties, express or implied, including any implied warranty of merchantability or fitness for a particular purpose,
      to the fullest extent permitted by Arizona law.
    </p>

    <h2 className="font-display text-xl font-bold text-[#000000] pt-4">Governing Documents</h2>
    <p>
      This page summarizes the warranty for informational purposes. The full, controlling warranty document is provided in writing
      with your signed installation agreement. In the event of any conflict, the signed written agreement governs.
    </p>

    <h2 className="font-display text-xl font-bold text-[#000000] pt-4">Questions or Claims</h2>
    <p>
      Call {BRAND.phone} or email {BRAND.email}. We answer every warranty call personally — no ticket queues, no runaround.
    </p>
  </Legal>
);

export const TermsPage = () => (
  <Legal title="Terms of Service" path="/terms-of-service">
    <p>Last updated: {new Date().toLocaleDateString("en-US", { month: "long", year: "numeric" })}</p>
    <h2 className="font-display text-xl font-bold text-[#000000] pt-4">Use of This Website</h2>
    <p>This website is provided by Vantyx Coatings LLC for informational purposes. Pricing shown is indicative "starting at" pricing; final pricing is provided in a written quote after an in-home measurement.</p>
    <h2 className="font-display text-xl font-bold text-[#000000] pt-4">Estimates & Services</h2>
    <p>Free estimates carry no obligation. Installations are governed by the written agreement signed at booking, including the Lifetime Limited Warranty terms and exclusions described therein.</p>
    <h2 className="font-display text-xl font-bold text-[#000000] pt-4">Limitation of Liability</h2>
    <p>Content on this site is provided "as is" without warranties of any kind. Vantyx Coatings LLC is not liable for decisions made based on informational content.</p>
    <h2 className="font-display text-xl font-bold text-[#000000] pt-4">Contact</h2>
    <p>Questions? Email {BRAND.email} or call {BRAND.phone}.</p>
  </Legal>
);
